import { useState, useCallback } from "react";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { ObraChecklistServicoHeader } from "../../domain/models/obra_checklist_servico_header";
import { InspecaoObra } from "../../domain/models/inspecao_obra";
import { initialObraItensChecklistAreaCadastroFilters, useObraItensChecklistAreaCadastroFilters } from "../hooks/ObraItensChecklistArea_cadastro/filter";
import { useObraItensChecklistAreaCadastroPagination } from "../hooks/ObraItensChecklistArea_cadastro/pagination";
import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { ObraChecklistServicoEstatisticas } from "../../domain/models/obra_checklist_servico_estatiticas";
import { InspecaoObraAprovacaoForm } from "../schemas/ObraItensChecklistArea_cadastro/inspecao_obra_aprovacao";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { FuncionarioInformation } from "../../../../common/domain/models/funcionario_information";
import { ObraChecklistServicoCampos } from "../../domain/models/obra_checklist_servico_campos";
import { InspecaoObraForm } from "../schemas/ObraItensChecklistArea_cadastro/inspecao_obra";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { ObraAreaChecklistHeader } from "../../domain/models/obra_area_checklist_header";

/**
 * LoadInspecaoObraFuncState
 */

type LoadInspecaoObraFuncState = {
  idObraChecklistServico?: number;
  data?: Date | null;
  localizacao?: string | null;
  status?: number | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useObraItensChecklistAreaCadastroViewModel
 */

export const useObraItensChecklistAreaCadastroViewModel = () => {
  const location = useLocation();

  const { idUsuario } = useUserAuthStore();

  const {
    commons: {
      getFuncionarioByIdUsuarioUseCase,
    },
    reports: {
      getReportChecklistAreaUseCase,
    },
    obras: {
      getObraHeaderInfoUseCase,
      getObraAreaChecklistHeaderUseCase,
      getObraChecklistServicoHeaderUseCase,
      getAllInspecaoObraUseCase,
      getObraChecklistServicoStatisticasUseCase,
      updateStatusObraChecklistServicoUseCase,
      updateMetaObraChecklistServicoUseCase,
      updateInspecaoObraAprovacaoLoteUseCase,
      getObraChecklistServicoCamposUseCase,
      createInspecaoObraUseCase,
      updateInspecaoObraUseCase,
      deleteInspecaoObraUseCase,
      copyInspecaoObraUseCase,
    },
  } = useDependencyInjection();

  const {
    pushInfoNotification,
    pushWarnNotification,
    pushErrorNotification,
    setPageSaving,
  } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const debounceHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [funcionarioInformation, setFuncionarioInformation] = useState<FuncionarioInformation>();

  const [idObra, setIdObra] = useState<number>();
  const [idObraAreaChecklist, setIdObraAreaChecklist] = useState<number>();
  const [idObraChecklistServico, setIdObraChecklistServico] = useState<number>();

  const [obraAreaChecklistHeader, setObraAreaChecklistHeader] = useState<ObraAreaChecklistHeader>();
  const [obraChecklistServicoHeader, setObraChecklistServicoHeader] = useState<ObraChecklistServicoHeader>();

  const [inspecoesObra, setInspecoesObra] = useState<InspecaoObra[]>();
  const [selectedInspecaoObra, setSelectedInspecaoObra] = useState<InspecaoObra>();

  const [estatisticasChecklistServico, setEstatisticasChecklistServico] = useState<ObraChecklistServicoEstatisticas>();
  const [obraChecklistServicoCampos, setObraChecklistServicoCampos] = useState<ObraChecklistServicoCampos>();

  const [statusChecklistServico, setStatusChecklistServico] = useState<number>(0);
  const [metaChecklistServico, setMetaChecklistServico] = useState<number>(0);

  const [obraChecklistServicoGraficoModal, setObraChecklistServicoGraficoModal] = useState(false);

  const [inspecaoObraModal, setInspecaoObraModal] = useState(false);
  const [inspecaoObraAprovacaoModal, setInspecaoObraAprovacaoModal] = useState(false);
  const [inspecaoObraCopyModal, setInspecaoObraCopyModal] = useState(false);
  const [inspecaoObraDeleteModal, setInspecaoObraDeleteModal] = useState(false);

  const obrasTopHeaderState = useObrasTopHeaderState();

  const [obrasReportState, loadObrasCommonReportsData] = useObrasCommonReports({ idObra });
  const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

  const filters = useObraItensChecklistAreaCadastroFilters();
  const pagination = useObraItensChecklistAreaCadastroPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  /**
   * Funcoes de carregamento
   */

  const loadObraAreaChecklistHeader = useCallback(async (id?: number) => {
    try {
      const filteredIdObraAreaChecklist = id ?? idObraAreaChecklist;
      const response = await getObraAreaChecklistHeaderUseCase.getObraAreaChecklistHeader(filteredIdObraAreaChecklist);
      setObraAreaChecklistHeader(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da area!');
    }
  }, [
    idObraAreaChecklist,
    getObraAreaChecklistHeaderUseCase,
  ]);

  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObraHeaderInfo(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    idObra,
    getObraHeaderInfoUseCase,
  ]);

  const loadObraChecklistServicoHeader = useCallback(async (id?: number) => {
    try {
      const filteredIdObraChecklistServico = id ?? idObraChecklistServico;
      const response = await getObraChecklistServicoHeaderUseCase.getObraChecklistServicoHeader(filteredIdObraChecklistServico);
      setObraChecklistServicoHeader(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    idObraChecklistServico,
    getObraChecklistServicoHeaderUseCase,
  ]);

  const loadInspecaoObra = useCallback(async (fs?: LoadInspecaoObraFuncState) => {
    try {
      const filteredIdObraChecklistServico = fs?.idObraChecklistServico ?? idObraChecklistServico;
      const filteredData = evaluateParamValue(fs?.data, filters.data);
      const filteredLocalizacao = evaluateParamValue(fs?.localizacao, filters.localizacao);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);
      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllInspecaoObraUseCase.getAllInspecaoObra(
        filteredIdObraChecklistServico,
        filteredData,
        filteredLocalizacao,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setInspecoesObra(getAllInspecaoObraUseCase.items);
      pagination.setTotalItems(getAllInspecaoObraUseCase.totalItems);
    } catch (e: unknown) {
      setInspecoesObra(undefined);

      pagination.setTotalItems(0);

      throw new Error("Falha ao buscar dados da pagina!");
    }
  }, [
    idObraChecklistServico,
    filters,
    pagination,
    getAllInspecaoObraUseCase,
  ]);

  const loadObraChecklistServicoEstatisticas = useCallback(async (id?: number) => {
    try {
      const filteredIdObraChecklistServico = id ?? idObraChecklistServico;

      const response = await getObraChecklistServicoStatisticasUseCase.getObraChecklistServicoStatisticas(
        filteredIdObraChecklistServico,
      );

      setEstatisticasChecklistServico(response);

      setStatusChecklistServico(response.status);
      setMetaChecklistServico(response.percentual ?? 0);
    } catch (e: unknown) {
      setEstatisticasChecklistServico(undefined);

      setStatusChecklistServico(0);
      setMetaChecklistServico(0);

      throw new Error("Falha ao buscar dados da pagina!");
    }
  }, [
    idObraChecklistServico,
    getObraChecklistServicoStatisticasUseCase,
  ]);

  const loadFuncionarioInformation = useCallback(async () => {
    try {
      const response = await getFuncionarioByIdUsuarioUseCase.getFuncionarioByIdUsuario(idUsuario);
      setFuncionarioInformation(response ?? undefined);
    } catch (e: unknown) {
      setFuncionarioInformation(undefined);
      throw new Error('Falha ao buscar informações sobre o usuário!');
    }
  }, [
    idUsuario,
    getFuncionarioByIdUsuarioUseCase,
  ]);

  const loadObraChecklistServicoCampos = useCallback(async (id?: number) => {
    try {
      const filteredIdObraChecklistServico = id ?? idObraChecklistServico;
      const response = await getObraChecklistServicoCamposUseCase
        .getObraChecklistServicoCampos(filteredIdObraChecklistServico);
      setObraChecklistServicoCampos(response);
    } catch (e: unknown) {
      setObraChecklistServicoCampos(undefined);
      throw new Error('Falha ao buscar informações sobre o usuário!');
    }
  }, [
    idObraChecklistServico,
    getObraChecklistServicoCamposUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationObraId = params.get('id');
      const locationIdChecklistArea = params.get('idChecklistArea');
      const locationIdChecklistServico = params.get('idChecklistServico');
      const locIdObra = locationObraId ?? undefined;
      const locIdChecklistArea = locationIdChecklistArea ?? undefined;
      const locIdChecklistServico = locationIdChecklistServico ?? undefined;

      if (!locIdObra || locIdObra.length === 0) {
        setErrorMessage("Falha ao recuperar id da obra!");
        return;
      }
      setIdObra(Number(locIdObra));

      if (!locIdChecklistArea || locIdChecklistArea.length === 0) {
        setErrorMessage("Falha ao recuperar id de checklist de area!");
        return;
      }
      setIdObraAreaChecklist(Number(locIdChecklistArea));

      if (!locIdChecklistServico || locIdChecklistServico.length === 0) {
        setErrorMessage("Falha ao recuperar id de checklist de serviço!");
        return;
      }
      setIdObraChecklistServico(Number(locIdChecklistServico));

      await loadFuncionarioInformation();

      await loadObraHeaderInfo(Number(locIdObra));

      await loadObrasCommonReportsData(Number(locIdObra));

      await loadObraAreaChecklistHeader(Number(locIdChecklistArea));

      await loadObraChecklistServicoCampos(Number(locIdChecklistServico));

      await loadObraChecklistServicoHeader(Number(locIdChecklistServico));

      await loadObraChecklistServicoEstatisticas(Number(locIdChecklistServico));

      useObraItensChecklistAreaCadastroPagination.setState({ page: 1 });
      await loadInspecaoObra({ idObraChecklistServico: Number(locIdChecklistServico), page: 1 });

    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados das paginas!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: `/Obras_cadastroarea?id=${idObra}` });
  }, [
    idObra,
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    if (idObra && idObraAreaChecklist) {
      try {
        setLoading(true);

        await loadFuncionarioInformation();

        await loadObraHeaderInfo(idObra);

        await loadObrasCommonReportsData(idObra);

        await loadObraAreaChecklistHeader(idObraAreaChecklist);

        await loadObraChecklistServicoCampos(idObraChecklistServico);

        await loadObraChecklistServicoHeader(idObraChecklistServico);

        await loadObraChecklistServicoEstatisticas(idObraChecklistServico);

        await loadInspecaoObra({ idObraChecklistServico: idObraChecklistServico });

      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idObra,
    idObraAreaChecklist,
    idObraChecklistServico,
    loadFuncionarioInformation,
    loadObraHeaderInfo,
    loadObraAreaChecklistHeader,
    loadObraChecklistServicoCampos,
    loadObrasCommonReportsData,
    loadObraChecklistServicoHeader,
    loadObraChecklistServicoEstatisticas,
    loadInspecaoObra,
    pushErrorNotification,
  ]);

  const handleRowSelectionModelChange = useCallback((model: GridRowSelectionModel) => {
    setRowSelectionModel(model);
  }, [
  ]);

  const handleSubmitMetaChecklistServico = useCallback(async () => {
    if (idObraChecklistServico && estatisticasChecklistServico) {
      try {
        setLoading(true);
        setPageSaving(true);

        await updateMetaObraChecklistServicoUseCase.updateMetaObraChecklistServico(
          idObraChecklistServico, metaChecklistServico);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        setMetaChecklistServico(estatisticasChecklistServico.percentual ?? 0);
        pushErrorNotification("Erro", processError(e, "Falha ao atualizar status do checklist!"));
      } finally {
        setPageSaving(false);
        setLoading(false);
      }
    }
  }, [
    idObraChecklistServico,
    estatisticasChecklistServico,
    metaChecklistServico,
    updateMetaObraChecklistServicoUseCase,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleInspecaoObraAprovacaoOpenState = useCallback((openState: boolean) => {
    if (openState) {
      if (!rowSelectionModel || !rowSelectionModel.ids.size) {
        pushWarnNotification("Alerta", "Você deve selecionar pelo menos um checklist para ser aprovado!");
        return;
      }
    }
    setInspecaoObraAprovacaoModal(openState);
  }, [
    rowSelectionModel,
    pushWarnNotification,
  ]);

  const handleInspecaoObraAprovacaoSubmit = useCallback(async (form: InspecaoObraAprovacaoForm) => {
    if (funcionarioInformation && funcionarioInformation.idFuncionario && rowSelectionModel && rowSelectionModel.ids.size) {
      try {
        setSaving(true);
        setPageSaving(true);

        await updateInspecaoObraAprovacaoLoteUseCase.updateInspecaoObraAprovacaoLote(
          funcionarioInformation.idFuncionario, rowSelectionModel.ids, form.data);

        await loadInspecaoObra();

        setRowSelectionModel({
          type: 'include',
          ids: new Set<GridRowId>(),
        });

        setInspecaoObraAprovacaoModal(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, ''));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    funcionarioInformation,
    rowSelectionModel,
    updateInspecaoObraAprovacaoLoteUseCase,
    loadInspecaoObra,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleInspecaoObraOpenState = useCallback((idInspecaoObra: number | null, openState: boolean) => {
    if (openState) {
      if (idInspecaoObra && inspecoesObra) {
        setSelectedInspecaoObra(inspecoesObra.find(x => x.idInspecaoObra === idInspecaoObra));
      } else {
        setSelectedInspecaoObra(undefined);
      }
    }
    setInspecaoObraModal(openState);
  }, [
    inspecoesObra,
  ]);

  const handleInspecaoObraSubmit = useCallback(async (form: InspecaoObraForm) => {
    if (idObraChecklistServico) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedInspecaoObra) {
          await updateInspecaoObraUseCase.updateInspecaoObra(
            selectedInspecaoObra.idInspecaoObra,
            form.data,
            form.campo1,
            form.campo2,
            form.campo3,
            form.campo4,
          );
        } else {
          await createInspecaoObraUseCase.createInspecaoObra(
            idObraChecklistServico,
            form.data,
            form.campo1,
            form.campo2,
            form.campo3,
            form.campo4,
          );
        }

        await loadObraChecklistServicoEstatisticas();
        await loadInspecaoObra();

        setInspecaoObraModal(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar verificação!'));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    idObraChecklistServico,
    selectedInspecaoObra,
    createInspecaoObraUseCase,
    updateInspecaoObraUseCase,
    loadObraChecklistServicoEstatisticas,
    loadInspecaoObra,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleInspecaoObraCopy = useCallback((idInspecaoObra: number | null, openState: boolean) => {
    if (openState) {
      if (idInspecaoObra && inspecoesObra) {
        setSelectedInspecaoObra(inspecoesObra.find(x => x.idInspecaoObra === idInspecaoObra));
      } else {
        setSelectedInspecaoObra(undefined);
      }
    }
    setInspecaoObraCopyModal(openState);
  }, [
    inspecoesObra,
  ]);

  const handleInspecaoObraCopyConfirm = useCallback(async (copyNumber: number) => {
    if (selectedInspecaoObra) {
      try {
        setSaving(true);
        setPageSaving(true);

        await copyInspecaoObraUseCase.copyInspecaoObra(
          selectedInspecaoObra.idInspecaoObra,
          copyNumber,
        );

        await loadObraChecklistServicoEstatisticas();
        await loadInspecaoObra();

        setInspecaoObraCopyModal(false);

        pushInfoNotification('Sucesso', 'Registro copiado com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao copiar verificação!'));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    selectedInspecaoObra,
    copyInspecaoObraUseCase,
    loadObraChecklistServicoEstatisticas,
    loadInspecaoObra,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleInspecaoObraDeletar = useCallback((idInspecaoObra: number | null, openState: boolean) => {
    if (openState) {
      if (idInspecaoObra && inspecoesObra) {
        setSelectedInspecaoObra(inspecoesObra.find(x => x.idInspecaoObra === idInspecaoObra));
      } else {
        setSelectedInspecaoObra(undefined);
      }
    }
    setInspecaoObraDeleteModal(openState);
  }, [
    inspecoesObra,
  ]);

  const handleInspecaoObraDeletarConfirm = useCallback(async () => {
    if (selectedInspecaoObra) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteInspecaoObraUseCase.deleteInspecaoObra(selectedInspecaoObra.idInspecaoObra);

        await loadObraChecklistServicoEstatisticas();
        await loadInspecaoObra();

        setInspecaoObraDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir verificação!'));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    selectedInspecaoObra,
    deleteInspecaoObraUseCase,
    loadObraChecklistServicoEstatisticas,
    loadInspecaoObra,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleChecklistAreaReport = useCallback(async (idInspecaoObra: number) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportChecklistAreaUseCase.getReportChecklistArea(requiredMimeType, idInspecaoObra)
      });

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao regrar relatorio!'));
    } finally {
      setLoading(false);
    }
  }, [
    getReportChecklistAreaUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  const handleObraChecklistServicoGraficoOpenState = useCallback((openState: boolean) => {
    setObraChecklistServicoGraficoModal(openState);
  }, [
  ]);

  const handleEnterInspecaoObra = useCallback((idInspecaoObra: number, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/ObraItensChecklistArea_verificacao?id=${idObra}&idChecklistArea=${idObraAreaChecklist}&idChecklistServico=${idObraChecklistServico}&idInspecaoObra=${idInspecaoObra}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/ObraItensChecklistArea_verificacao?id=${idObra}&idChecklistArea=${idObraAreaChecklist}&idChecklistServico=${idObraChecklistServico}&idInspecaoObra=${idInspecaoObra}`,
        { alternativeRoute: `/ObraItensChecklistArea_cadastro?id=${idObra}&idChecklistArea=${idObraAreaChecklist}&idChecklistServico=${idObraChecklistServico}` });
    }
  }, [
    idObra,
    idObraAreaChecklist,
    idObraChecklistServico,
    backOrNavigate,
  ]);

  const handleChecklistServicoStatusChange = useCallback(async (value: number) => {
    if (idObraChecklistServico) {
      try {
        setLoading(true);

        await updateStatusObraChecklistServicoUseCase.updateStatusObraChecklistServico(
          idObraChecklistServico, value);

        setStatusChecklistServico(value);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao atualizar status do checklist!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idObraChecklistServico,
    updateStatusObraChecklistServicoUseCase,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleChecklistServicoMetaChange = useCallback((value: number) => {
    setMetaChecklistServico(value);
  }, [
  ]);

  const handleDataChange = useDebouncedCallback(debounceHandler, async (data: Date | null) => {
    useObraItensChecklistAreaCadastroFilters.setState({ data });
    return async () => {
      try {
        setLoading(true);
        useObraItensChecklistAreaCadastroPagination.setState({ page: 1 });
        await loadInspecaoObra({ data, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadInspecaoObra,
    pushErrorNotification,
  ]);

  const handleLocalizacaoChange = useDebouncedCallback(debounceHandler, async (localizacao: string | null) => {
    useObraItensChecklistAreaCadastroFilters.setState({ localizacao });
    return async () => {
      try {
        setLoading(true);
        useObraItensChecklistAreaCadastroPagination.setState({ page: 1 });
        await loadInspecaoObra({ localizacao, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadInspecaoObra,
    pushErrorNotification,
  ]);

  const handleStatusChange = useDebouncedCallback(debounceHandler, async (status: number) => {
    useObraItensChecklistAreaCadastroFilters.setState({ status });
    return async () => {
      try {
        setLoading(true);
        useObraItensChecklistAreaCadastroPagination.setState({ page: 1 });
        await loadInspecaoObra({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadInspecaoObra,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useObraItensChecklistAreaCadastroFilters.setState({ ...initialObraItensChecklistAreaCadastroFilters });
      useObraItensChecklistAreaCadastroPagination.setState({ page: 1 });
      await loadInspecaoObra({ ...initialObraItensChecklistAreaCadastroFilters, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadInspecaoObra,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useObraItensChecklistAreaCadastroPagination.setState({ page: page + 1 });
      await loadInspecaoObra({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadInspecaoObra,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useObraItensChecklistAreaCadastroPagination.setState({ page: 1, rowsPerPage });
      await loadInspecaoObra({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadInspecaoObra,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    funcionarioInformation: funcionarioInformation ?? null,
    idObra,
    idObraAreaChecklist,
    obraAreaChecklistHeader: obraAreaChecklistHeader ?? null,
    obraChecklistServicoHeader,
    inspecoesObra: inspecoesObra ?? [],
    selectedInspecaoObra: selectedInspecaoObra ?? null,
    estatisticasChecklistServico: estatisticasChecklistServico ?? null,
    obraChecklistServicoCampos: obraChecklistServicoCampos ?? null,
    statusChecklistServico,
    metaChecklistServico,
    obraChecklistServicoGraficoModal,
    inspecaoObraModal,
    inspecaoObraAprovacaoModal,
    inspecaoObraCopyModal,
    inspecaoObraDeleteModal,
    obrasTopHeaderState,
    obrasReportState,
    selectedObraHeaderInfo,
    rowSelectionModel,
    filters,
    pagination,
    reportViewerState,
    handleBack,
    handleReload,
    handleRowSelectionModelChange,
    handleSubmitMetaChecklistServico,
    handleInspecaoObraAprovacaoOpenState,
    handleInspecaoObraAprovacaoSubmit,
    handleInspecaoObraOpenState,
    handleInspecaoObraSubmit,
    handleInspecaoObraCopy,
    handleInspecaoObraCopyConfirm,
    handleInspecaoObraDeletar,
    handleInspecaoObraDeletarConfirm,
    handleChecklistAreaReport,
    handleObraChecklistServicoGraficoOpenState,
    handleEnterInspecaoObra,
    handleChecklistServicoStatusChange,
    handleChecklistServicoMetaChange,
    handleDataChange,
    handleLocalizacaoChange,
    handleStatusChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
  };
}